import { GanttComponent } from '@awell/ui-kit/components/Gantt'
import type { GanttData, GanttFilters } from '@awell/ui-kit/components/Gantt/types'
import { TimelineFilters } from './TimelineFilters'
import { isNil } from 'lodash'
import React, { type FC, useEffect, useState } from 'react'
import { t } from 'i18next'
import { type Element, usePathwayElements } from '../../hooks/usePathwayElements'
import { usePathwayContextProvider } from '../PathwayContextProvider'
import { TimelineSkeleton } from './skeletons/TimelineSkeleton'
import { ElementStatus, ElementType } from './types'
import { usePreviewGrid } from './useStyles'

const convertElementDataToGantt = (data: Array<Element>): Array<GanttData> => {
  return data.map((e: Element) => ({
    id: e.id,
    parent: e.parent_id ?? undefined,
    text: e.name,
    element_type: e.type,
    activity_type: e.activity_type ?? undefined,
    start_date: new Date(e.start_date),
    end_date: isNil(e.end_date) ? new Date() : new Date(e.end_date),
    status: e.status,
    stakeholder: e.stakeholders?.map(({ name }) => name)?.join(' ') || '-',
    stakeholders: e.stakeholders,
    context: e.context,
    open: true
  }))
}

interface TimelineProps {
  pathwayId: string
}

export const Timeline: FC<TimelineProps> = () => {
  const { elements, loading: elementsLoading } = usePathwayElements()
  const [data, setGanttData] = useState<Array<GanttData>>([])
  const grid = usePreviewGrid()

  const [appliedFilters, setFilters] = useState<GanttFilters & { showTracksTriggers?: boolean }>({
    query: '',
    type: [ElementType.Step, ElementType.Pathway, ElementType.Track, ElementType.Action],
    status: [],
    stakeholder: [],
    activity_type: [],
    showTracksTriggers: false
  })

  const { setPanelFromElement, loading: activitiesLoading } = usePathwayContextProvider()

  const handleElementClick = (element: GanttData) => {
    if (!isNil(elements)) {
      const selectedElement = elements.find(e => e.id === element.id)
      if (!isNil(selectedElement)) {
        setPanelFromElement(selectedElement)
      }
    }
  }

  useEffect(() => {
    if (elements) {
      const convertedData = convertElementDataToGantt(
        elements.filter(({ status }) => ![ElementStatus.Discarded, ElementStatus.Postponed].includes(status))
      )
      setGanttData(convertedData)
    }
  }, [elements])

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (elementsLoading || activitiesLoading || !data) {
    return <TimelineSkeleton />
  }

  const handleFiltersChange = (filters: GanttFilters & { showTracksTriggers?: boolean }) => {
    setFilters(filters)
  }

  return (
    <div className={grid.container}>
      <TimelineFilters
        data={data}
        appliedFilters={appliedFilters}
        onFiltersChange={handleFiltersChange}
        loading={elementsLoading || activitiesLoading}
      />
      <div className={grid.previewContent}>
        <GanttComponent data={data} links={[]} onElementClick={handleElementClick} filters={appliedFilters} />
      </div>
    </div>
  )
}

Timeline.displayName = 'Timeline'
